import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';
import { format, isDate } from 'date-fns'
// next
import { useRouter } from 'next/router';
// form
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Typography,
  Stack,
  InputAdornment,
  TextField,
  Checkbox,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers-pro';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive'
// components
import { FormProvider, RHFTextField } from '../../components/hook-form';
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import DIOText from '../../components/DIOText';
import {
  CoreTimeDialog,
} from '../profile';
import {
  ConfirmNegoPlanDialog,
} from './';
import FeeTaxDialog from "./FeeTaxDialog";
// utils
import axios from '../../utils/axios';

// ----------------------------------------------------------------------

const GreyStyles = styled('div')(({ theme }) => ({
  padding: theme.spacing(2.5),
  marginBottom: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.neutral,
}));

const InfoText = styled('span')(({ theme }) => ({
  color: theme.palette.info.main,
  fontWeight: 'bold',
}));

// ----------------------------------------------------------------------

export default function NegotiationOfferCard({ offer, reloadOffer, back }) {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { oid } = router.query;
  const smUp = useResponsive('up', 'sm');
  const [isOpenCoretimeDialog, setIsOpenCoretimeDialog] = useState(false);
  const [isOpenConfirmNegoPlanDialog, setIsOpenConfirmNegoPlanDialog] = useState(false);
  const [isOpenFeeTaxDialog, setIsOpenFeeTaxDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tmpCoreTime, setTmpCoreTime] = useState({});

  // 다이얼로그 안내용 금액들
  const [trialSalary, setTrialSalary] = useState('');
  const [trialWorkingHours, setTrialWorkingHours] = useState('');
  const hasTrial = trialSalary && trialWorkingHours;
  const [longtermSalary, setLongtermSalary] = useState('');
  const [longtermWorkingHours, setLongtermWorkingHours] = useState('');
  const [isAccountMonitored, setIsAccountMonitored] = useState(false);
  const [confirmData, setConfirmData] = useState({});

  const twoDaysLater = new Date();
  twoDaysLater.setDate(twoDaysLater.getDate() + 2);
  twoDaysLater.setHours(0, 0, 0, 0);
  const twoDaysLaterTimezoneCorrection = new Date(twoDaysLater.getTime() + (twoDaysLater.getTimezoneOffset() > 0 ? twoDaysLater.getTimezoneOffset() * 60000 : 0));

  const parseAbsDate = (dateStr) => {
    if (!dateStr) return null;
    if (dateStr instanceof Date) return dateStr;
    const [y, m, d] = dateStr.split('-');
    if (y && m && d) {
      return new Date(parseInt(y), parseInt(m)-1, parseInt(d), 0, 0, 0);
    }
    return null;
  };

  const parseDateString = (value, originalValue) => {
    const parseTarget = isDate(originalValue) ? originalValue.toISOString().substring(0, 10) : originalValue ? originalValue : twoDaysLaterTimezoneCorrection;
    return parseAbsDate(parseTarget);
  };

  const openCoretimeDialog = () => {
    setIsOpenCoretimeDialog(true);
  };

  const calculateTrialPlan = async () => {
    const hours = Number.parseInt(getValues("workingHours"));
    const salary = Number.parseInt(getValues("salary"));

    if (!hours || !salary || hours > offer.available_hours) {
      setTrialSalary('');
      setTrialWorkingHours('');
      return;
    }

    // trial and longterm
    if (offer?.longterm_salary > 0 && offer?.longterm_working_hours_per_week > 0) {
      setTrialSalary(Math.floor(salary/2));
      setTrialWorkingHours(hours);
      setLongtermSalary(salary*2);
      setLongtermWorkingHours(hours);
    } else {
      // only trial
      if (salary <= 50) {
        setTrialSalary(salary);
        setTrialWorkingHours(hours);
        return;
      }

      if (salary <= 100) {
        setTrialSalary(50);
        setTrialWorkingHours(hours);
        return;
      }

      const multiplier = 100/(Number.parseInt(salary));
      const calTrialSalary = 50;
      const calTrialWorkingHours = Math.max(1, Math.floor(hours*multiplier));
      setTrialSalary(calTrialSalary);
      setTrialWorkingHours(calTrialWorkingHours);
    }
  };

  const OfferResponseSchema = Yup.object().shape({
    startDate: Yup.date().transform(parseDateString).required('업무 시작 가능 일자를 입력해주세요').min(twoDaysLaterTimezoneCorrection, '2일 이후 일자를 입력해주세요'),
    salary: Yup.number().typeError('숫자를 입력해주세요').positive('양수를 입력해주세요').required('4주 인건비를 입력해주세요').max(99999, '단위는 만원입니다. 5자리 이하로 입력해주세요'),
    workingHours: Yup.number().typeError('숫자를 입력해주세요').positive('양수를 입력해주세요').required('주당 업무 투입 시간을 입력해주세요').max(offer.available_hours, '현재 업무 가능 시간 이하로 입력해주세요'),
    task: Yup.string().required('업무 내용을 입력해주세요'),
    coreTime: Yup.array().of(Yup.object().shape({ day: Yup.string(), start: Yup.object(), end: Yup.object(), weekday: Yup.number() })).min(1, '코어 타임을 입력해주세요'),
  });

  const defaultValues = {
    startDate: parseAbsDate(offer?.nego_start_date) || twoDaysLaterTimezoneCorrection,
    salary: offer.nego_salary || 0,
    workingHours: offer.nego_working_hours_per_week || 0,
    task: offer.nego_task || '',
    coreTime: offer.nego_core_time || [],
  };

  const taskPlaceholder = `사전 미팅에서 파악한 내용을 바탕으로 간단히 작성해주세요.`;

  const methods = useForm({
    resolver: yupResolver(OfferResponseSchema),
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    trigger,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = methods;

  const watchValues = async (value, name, _type) => {
    if (name === 'workingHours') {
      trigger('workingHours');
      calculateTrialPlan();
    }
    if (name === 'salary') {
      calculateTrialPlan(value);
    }
    if (name === 'coreTime') {
      trigger('coreTime');
    }
  }

  const setCoreTime = (coreTime) => {
    setValue('coreTime', coreTime);
    setIsOpenCoretimeDialog(false);
  }

  useEffect(() => {
    const subscription = watch((value, { name, type }) => watchValues(value, name, type ));
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    calculateTrialPlan();
  }, [offer]);

  const sendNegotiation = async () => {
    try {
      setIsSubmitting(true);
      const negoData = getValues();
      await axios.post(`api/crews/offers/${oid}/negotiate/`, {
        nego_start_date: format(negoData.startDate, 'yyyy-MM-dd'),
        nego_salary: negoData.salary,
        nego_working_hours_per_week: negoData.workingHours,
        nego_task: negoData.task,
        nego_core_time: tmpCoreTime,  // onSubmit에서 제출 안하고 다이얼로그 하나 더 띄운 다음에 get values 를 하면 json parsing 이슈가 있어서 값을 따로 임시 저장
      });
      enqueueSnackbar('계약조건을 제안하였습니다');
      reloadOffer();
      setIsOpenConfirmNegoPlanDialog(false);
      setIsSubmitting(false);
    } catch (error) {
      enqueueSnackbar('계약조건을 제안에 실패하였습니다. 관리자에 문의해주세요.', { variant: 'error' });
      console.error(error);
      setIsSubmitting(false);
    }
  }

  const onSubmit = async (data) => {
    if (offer?.is_account_monitored && !isAccountMonitored) {
      enqueueSnackbar('업무 계정 지급 및 활동 모니터링 안내에 동의해주세요.', { variant: 'error' });
      return;
    }
    try {
      setTmpCoreTime(data.coreTime);
      setConfirmData({
        workingHours: Number.parseInt(getValues("workingHours")),
        salary: Number.parseInt(getValues("salary")),
        trialSalary,
        trialWorkingHours,
        longtermSalary,
        longtermWorkingHours,
      })
      setIsOpenConfirmNegoPlanDialog(true);
    } catch (error) {
      console.error(error);
    }
  };

  const onError = (error) => {
    enqueueSnackbar('계약조건을 다시 확인하고 수정한 후 제출해주세요.', { variant: 'error' });
  }

  return (
    <>
      {offer.offer_status === "company-negotiate-rewrite" && (
        <GreyStyles>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            🙋‍♂️ 계약조건을 수정해서 다시 제안해주세요!
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            회사의 수정 요청 내용: {offer.nego_comment}
          </Typography>
        </GreyStyles>
      )}

      <FormProvider methods={methods}>
        <Stack spacing={3}>
          <Stack direction={smUp ? "row" : "column"} spacing={3}>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <RHFTextField
                name="salary"
                label="4주 인건비"
                labelVariant='subtitle1'
                type="number"
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">만원</InputAdornment>,
                }}
                fullWidth
                placeholder="000"
              />
              <Button
                size="small"
                sx={{
                  mt: 2,
                  maxWidth: 'max-content',
                  color: 'text.secondary',
                }}
                fullWidth={false}
                startIcon={<Iconify icon={'eva:info-fill'} />}
                onClick={() => setIsOpenFeeTaxDialog(true)}
              >
                수수료 & 세금 정책
              </Button>
            </Stack>
            <Stack spacing={1} sx={{ width: '100%' }}>
              <Stack direction="row" alignItems="center" spacing={.5}>
                <Typography variant="subtitle1">
                  주당 업무 투입 시간
                </Typography>
                <Typography variant="body2">
                  (최대 <InfoText>{offer?.available_hours}시간</InfoText> 입력 가능)
                </Typography>
              </Stack>
              <RHFTextField
                name="workingHours"
                label=""
                labelVariant='subtitle1'
                type="number"
                onWheel={(e) => e.target.blur()}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                    e.preventDefault();
                  }
                }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">시간</InputAdornment>,
                }}
                fullWidth
                placeholder="0"
                helperText={`${user?.crew?.current_working_hours_per_week} / ${user?.crew?.limit_working_hours_per_week}시간 일하는 중 ${offer?.matching_hours ? ` • ${offer?.matching_hours}시간 계약 논의 중` : ''}`}
              />
            </Stack>
          </Stack>

          {/* trial box */}
          {!offer?.longterm_salary && !offer?.longterm_working_hours_per_week && (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "grey.100",
            }}
          >
            <Typography variant="h6" gutterBottom>
              2주 50만원 체험판 플랜 적용시
            </Typography>
            <Typography variant="body2" gutterBottom>
              회사는 크루와 4주 계약 전에 2주 체험판 플랜을 선택할 수 있어요. <br />
              회사가 2주 체험판 플랜을 선택하면 크루는 제안한 4주 인건비를 시급 계산하여 2주 동안 50만원 만큼 일해보고 계약을 연장할 수 있어요.
            </Typography>
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              sx={{
                border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                borderRadius: 1,
                p: 2,
              }}
            >
              <SvgIconStyle src={'/icons/ic_clock.svg'} sx={{ minWidth: 24, minHeight: 24, color: hasTrial ? "text.secondary" : "text.disabled" }} />
              {!hasTrial && (
                <Typography sx={{ color: "text.disabled" }}>
                  주당 업무 시간과 인건비를 입력하면 자동 계산됩니다.
                </Typography>
              )}
              {hasTrial && (
                <Typography>
                  <b>주 {trialWorkingHours}시간</b> {!smUp && (<br/>)}(2주 총 {trialWorkingHours*2}시간) 근무
                </Typography>
              )}
            </Stack>
            {hasTrial && smUp && (
              <Typography sx={{ mt: 0.5, ml: 2, color: "grey.600" }} variant="body2">
                주당 업무 가능 시간이나 4주 인건비를 수정하면 2주 50만원 체험판 근무 시간도 자동 변동됩니다.
              </Typography>
            )}
          </Box>
          )}
          {/* trial + longter box */}
          {offer?.longterm_salary > 0 && offer?.longterm_working_hours_per_week > 0 && (
          <Box
            sx={{
              p: 3,
              borderRadius: 2,
              backgroundColor: "grey.100",
            }}
          >
            <Stack sx={{ width: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
                <SvgIconStyle src={`/icons/ic_calendar.svg`} sx={{ color: "text.secondary", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
                <Typography variant="subtitle2" gutterBottom>
                  2주, 8주 플랜 안내
                </Typography>
              </Stack>
              <Typography variant="body2" sx={{ mb: .5 }}>
                회사는 2주, 4주, 8주의 계약기간 중 하나를 선택할 수 있습니다.
              </Typography>
              <ul style={{ marginLeft: "16px" }}>
                <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                  <Typography variant="caption">
                    2주 또는 8주 계약시 크루가 제안한 4주 인건비를 기준으로 기간에 맞게 조정합니다. (2주: 50%, 8주: 200%)
                  </Typography>
                </li>
                <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                  <Typography variant="caption">
                    모든 플랜의 주당 업무 시간은 동일합니다.
                  </Typography>
                </li>
                <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                  <Typography variant="caption">
                    상주 업무는 회사에서 4주, 8주 플랜만 선택할 수 있어요.
                  </Typography>
                </li>
              </ul>
            </Stack>
          </Box>
          )}

          <Stack spacing={1} sx={{ width: '100%' }}>
            <Typography variant="subtitle1">
              업무 시작 가능일
            </Typography>
            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DatePicker
                  {...field}
                  minDate={twoDaysLaterTimezoneCorrection}
                  inputFormat="yyyy-MM-dd"
                  mask={"____-__-__"}
                  value={field.value}
                  onChange={(newValue) => {
                    field.onChange(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField {...params} fullWidth error={!!error} helperText={error?.message || "업무 시작 가능일 이후로 회사가 계약 기간을 설정합니다 "} />
                  )}
                />
              )}
            />
          </Stack>

          <Stack sx={{ width: '100%' }}>
            <Stack direction="row" spacing={.5} alignItems="center">
              <Typography variant="subtitle1">
                코어 타임 (한국 시간 KST 기준)
              </Typography>
            </Stack>
            <Typography variant="body2" sx={{ mt: 0.5, color: "text.secondary" }} gutterBottom>
              고정적으로 근무가 가능한 요일과 시간을 입력해주세요.
            </Typography>
            {(getValues().coreTime.length === 0 || Object.keys(getValues().coreTime).length === 0) && (
            <Box>
              <Button
                variant="outlined"
                startIcon={<Iconify icon="ic:round-plus" />}
                sx={{ borderRadius: 2 }}
                disableRipple
                onClick={openCoretimeDialog}
                color={errors.coreTime ? "error" : "primary"}
              >
                코어 타임 입력하기
              </Button>
            </Box>
            )}
            {getValues().coreTime.length > 0 && (
              <>
            <Box
              sx={{
                "&:hover": {
                  border: '1px solid black',
                },
                p: 1.5,
                border: (theme) => `1px solid ${theme.palette.grey[500_32]}`,
                borderRadius: 1,
              }}
            >
              {getValues().coreTime.map((coreTime, index) => (
                (typeof coreTime.start === 'object' && typeof coreTime.end === 'object') ? (
                  <Typography key={index}>
                    {coreTime.day} {coreTime.start?.label} ~ {coreTime.end?.label}
                  </Typography>
                ) : (
                  <Typography key={index}>
                    {coreTime.day} {JSON.parse(coreTime.start)?.label} ~ {JSON.parse(coreTime.end)?.label}
                  </Typography>
                )
              ))}
            </Box>
            <Box sx={{ mt: .5, justifyContent: "flex-end", display: "flex" }}>
              <Button
                variant="text"
                endIcon={<Iconify icon="ic:baseline-chevron-right" />}
                disableRipple
                onClick={openCoretimeDialog}
                sx={{ color: "text.secondary" }}
              >
                코어 타임 수정하기
              </Button>
            </Box>
            </>
            )}
            {errors.coreTime && getValues().coreTime.length === 0 && (
              <Typography variant="caption" sx={{ color: 'red', mt: 1, ml: 2 }}>
                {errors.coreTime.message}
              </Typography>
            )}
          </Stack>

          <Stack sx={{ width: '100%' }}>
            <RHFTextField
              label="업무 내용"
              labelVariant='subtitle1'
              name="task"
              multiline
              minRows={2}
              placeholder={taskPlaceholder}
            />
          </Stack>

          {/* 정책 안내 box */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderRadius: 1.5,
              backgroundColor: "grey.100",
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1.5 }}>
              정책 안내
            </Typography>
            <Stack direction={"column"} spacing={{ xs: 2, sm: 3 }} sx={{ mb: 1 }}>
              {/* 급여 정책 */}
              <Stack sx={{ width: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
                  <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: offer?.is_account_monitored ? "text.primary" : "info.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    급여 지급 정책
                  </Typography>
                </Stack>
                <Typography variant="overline" sx={{ mb: .5 }}>
                  <DIOText /> 급여청구서 기능으로 청구한 급여만 지급됩니다.
                </Typography>
                <ul style={{ marginLeft: "16px" }}>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      각 주차의 마지막날 자정까지 해당 주차의 급여를 청구해야합니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      기한 내에 청구하지 않으면, 기록된 근무 시간으로 자동 청구됩니다.<br/>
                      (근무 시간이 계약 시간을 넘을 경우, 계약 시간만큼만 청구됩니다.)
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      기록된 근무 시간 보다 더 많이 청구하는 경우 사유를 함께 제출해야합니다.
                    </Typography>
                  </li>
                </ul>
              </Stack>
              {/* 출퇴근 정책 */}
              <Stack sx={{ width: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: offer?.is_account_monitored ? "text.primary" : "oldprimary.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    출퇴근 정책
                  </Typography>
                </Stack>
                <Typography variant="overline" sx={{ mb: .5 }}>
                  회사와 계약 기간 동안 <DIOText /> 시스템 내 출퇴근 기능으로 업무를 기록해야합니다.
                </Typography>
                <ul style={{ marginLeft: "16px" }}>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      약속한 코어 타임에 근무해야하며, 출퇴근 일정 변경 시 회사에 사전 공유해주세요.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      코어타임 반복 미준수 시 기업이 업무 불성실로 간주하여 이의 제기 또는 계약 해지의 사유로 삼을 수 있으니 유의하여 주십시오.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      출퇴근 기록과 당일 진행한 업무는 실시간으로 <DIOText />와 회사에 공유됩니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      출근 시 해야할 업무를 기록해야 출근 기록이 시작되며, 퇴근 시 진행한 업무를 기록해야 퇴근 시간이 기록됩니다.
                    </Typography>
                  </li>
                </ul>
              </Stack>
              {/* 업무 계정 정책 */}
              {offer?.is_account_monitored && (
              <Stack sx={{ width: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: .5 }}>
                  <SvgIconStyle src={`/icons/ic_check.svg`} sx={{ color: "info.main", width: 16, height: 16, minWidth: 16, minHeight: 16 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    업무 계정 지급 및 활동 모니터링 안내
                  </Typography>
                </Stack>
                <Typography variant="overline" sx={{ mb: .5 }}>
                  <DIOText />에서 지급한 이메일 계정을 통해 진행된 작업만이 공식 업무로 인정되며, <DIOText />는 지급한 업무용 이메일 계정을 통해 이루어진 활동을 업무 효율 향상 및 품질 관리를 위해 열람/기록/분석할 수 있습니다.
                </Typography>
                <ul style={{ marginLeft: "16px" }}>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      <DIOText />는 효율적인 협업과 업무 관리 목적을 위해, 모든 크루에게 업무용 이메일 계정을 제공합니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      해당 계정은 Notion, Slack, Figma, Github, Gmail 등 모든 업무 툴에 연동되어야 하며, 이를 통해 업무 로그 및 협업 활동이 기록됩니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      해당 계정을 통해 진행된 작업만이 공식 업무로 인정됩니다.
                    </Typography>
                  </li>
                  <li style={{ fontSize: "0.5rem", marginBottom: "2px" }}>
                    <Typography variant="caption">
                      <DIOText />는 지급한 업무용 이메일 계정을 통해 이루어진 활동을 업무 효율 향상 및 품질 관리를 위해 열람/기록/분석할 수 있습니다.
                    </Typography>
                  </li>
                </ul>
                <FormGroup>
                  <FormControlLabel
                    control={<Checkbox checked={isAccountMonitored} onClick={() => setIsAccountMonitored(!isAccountMonitored)} />}
                    label="네. 동의합니다."
                  />
                </FormGroup>
              </Stack>
              )}
            </Stack>
          </Box>

          <Stack sx={{ width: 1 }} direction={{xs: "column-reverse", sm: "row"}} justifyContent="flex-end" spacing={2}>
            <LoadingButton type="submit" variant="outlined" loading={isSubmitting} onClick={back} fullWidth={!smUp}>
              이전
            </LoadingButton>
            <LoadingButton type="submit" variant="contained" loading={isSubmitting} onClick={handleSubmit(onSubmit, onError)} fullWidth={!smUp}>
              계약조건 제안하기
            </LoadingButton>
          </Stack>
        </Stack>

        <CoreTimeDialog
          isOpen={isOpenCoretimeDialog}
          closeDialog={() => setIsOpenCoretimeDialog(false)}
          setCoreTime={setCoreTime}
          initialCoreTime={getValues().coreTime}
        />
        <ConfirmNegoPlanDialog
          isOpen={isOpenConfirmNegoPlanDialog}
          closeDialog={() => setIsOpenConfirmNegoPlanDialog(false)}
          confirm={sendNegotiation}
          workType={offer?.work_type}
          confirmData={confirmData}
          isSubmitting={isSubmitting}
        />
        <FeeTaxDialog isOpen={isOpenFeeTaxDialog} close={() => setIsOpenFeeTaxDialog(false)} />
      </FormProvider>
    </>
  );
}
